
import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface RankingEntry {
  id: string;
  rank: number;
  name: string;
  studyTime: number;
  avatarColor: string;
  isCurrentUser: boolean;
}

interface RankingWidgetProps {
  layoutMode?: 'normal' | 'compact';
}

const AVATAR_COLORS = [
  'from-yellow-400 to-orange-500',
  'from-pink-400 to-rose-500',
  'from-green-400 to-teal-500',
  'from-indigo-400 to-purple-500',
  'from-blue-400 to-cyan-500',
  'from-red-400 to-pink-500',
];

export default function RankingWidget({ layoutMode = 'normal' }: RankingWidgetProps) {
  const { user } = useAuth();
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchRankingData();
    }
  }, [user]);

  const fetchRankingData = async () => {
    if (!user) return;

    try {
      const { data: entries } = await supabase
        .from('diary_entries')
        .select('user_id, study_hours');

      const userTotalHours: { [key: string]: number } = {};
      const allUserIds = new Set<string>();

      entries?.forEach((entry) => {
        if (entry.user_id) {
          allUserIds.add(entry.user_id);
          userTotalHours[entry.user_id] = (userTotalHours[entry.user_id] || 0) + (entry.study_hours || 0);
        }
      });

      const sorted = Array.from(allUserIds)
        .map((userId) => ({
          user_id: userId,
          total_hours: userTotalHours[userId] || 0,
        }))
        .filter(u => u.total_hours > 0)
        .sort((a, b) => b.total_hours - a.total_hours)
        .slice(0, 10);

      // プロフィール取得
      const userIds = sorted.map(r => r.user_id);
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username')
        .in('user_id', userIds);

      const profileMap: { [key: string]: string } = {};
      profiles?.forEach(p => {
        profileMap[p.user_id] = p.username || 'Unknown';
      });

      const rankingList: RankingEntry[] = sorted.map((r, index) => ({
        id: r.user_id,
        rank: index + 1,
        name: profileMap[r.user_id] || 'Unknown',
        studyTime: r.total_hours,
        avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
        isCurrentUser: r.user_id === user.id,
      }));

      setRankings(rankingList);
    } catch (error) {
      console.error('Error fetching ranking data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-medal-line text-yellow-500"></i>
        学習ランキング
      </h3>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
        </div>
      ) : rankings.length === 0 ? (
        <p className="text-center text-gray-400 dark:text-gray-500 py-6 text-sm">データがありません</p>
      ) : (
        <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
          {rankings.map((entry, index) => (
            <div
              key={entry.id}
              className={`flex items-center gap-3 ${layoutMode === 'compact' ? 'p-2' : 'p-3'} ${
                entry.isCurrentUser
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-200 dark:border-indigo-800'
                  : 'bg-gray-50 dark:bg-gray-700/50'
              } rounded-lg`}
            >
              <div className={`font-bold ${
                index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-orange-600' : 'text-gray-500'
              } ${layoutMode === 'compact' ? 'w-6 text-center' : 'w-8 text-center text-lg'}`}>
                {index < 3 ? <i className="ri-trophy-fill"></i> : entry.rank}
              </div>
              <div className={`${layoutMode === 'compact' ? 'w-8 h-8' : 'w-10 h-10'} rounded-full bg-gradient-to-br ${entry.avatarColor} flex items-center justify-center text-white font-bold`}>
                {entry.name.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800 dark:text-white">{entry.name}</p>
                <p className={`text-gray-500 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                  {entry.studyTime}時間
                </p>
              </div>
              {entry.isCurrentUser && (
                <span className={`bg-indigo-600 text-white font-medium rounded-full ${layoutMode === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'}`}>
                  あなた
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
